import { useCallback, useMemo, useState } from 'react';

/**
 * Ids for override-table rows that were added from a picker but are not yet
 * committed to the draft (no value entered). Keeps the row visible without
 * flipping the unsaved-changes bar — same idea as OemOverridesTable's
 * historical `emptyRowMakeIds`.
 */
export function useEmptyOverrideRows<Id extends string = string>() {
    const [emptyRowIds, setEmptyRowIds] = useState<Array<Id>>([]);

    const addEmptyRows = useCallback((ids: Iterable<Id>) => {
        setEmptyRowIds((prev) => {
            const seen = new Set(prev);
            const next = [...prev];
            Array.from(ids).forEach((id) => {
                if (!seen.has(id)) {
                    seen.add(id);
                    next.push(id);
                }
            });
            return next;
        });
    }, []);

    const removeEmptyRow = useCallback((id: Id) => {
        setEmptyRowIds((prev) => {
            if (!prev.includes(id)) return prev;
            return prev.filter((x) => x !== id);
        });
    }, []);

    const hasEmptyRow = useCallback(
        (id: Id) => emptyRowIds.includes(id),
        [emptyRowIds],
    );

    /** Drop ids that now appear in the committed/listed set. */
    const pruneCommitted = useCallback((committedIds: Iterable<Id>) => {
        const committed = committedIds instanceof Set
            ? committedIds
            : new Set(Array.from(committedIds));
        setEmptyRowIds((prev) => {
            const next = prev.filter((id) => !committed.has(id));
            return next.length === prev.length ? prev : next;
        });
    }, []);

    return useMemo(
        () => ({
            emptyRowIds,
            addEmptyRows,
            removeEmptyRow,
            hasEmptyRow,
            pruneCommitted,
        }),
        [emptyRowIds, addEmptyRows, removeEmptyRow, hasEmptyRow, pruneCommitted],
    );
}

/**
 * Append synthetic empty rows for ids that are not already in `listed`.
 * Callers sort the result if needed.
 */
export function mergeEmptyOverrideRows<T, Id extends string>(
    listed: ReadonlyArray<T>,
    emptyRowIds: ReadonlyArray<Id>,
    getId: (row: T) => Id,
    createEmpty: (id: Id) => T | null,
): Array<T> {
    const listedIds = new Set(listed.map(getId));
    const extras = emptyRowIds
        .filter((id) => !listedIds.has(id))
        .map(createEmpty)
        .filter((row): row is T => row != null);
    return [...listed, ...extras];
}
